var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { ChevronDown } from '@volkswagen-onehub/icons-core';
import { Text, TextTag, TokenTextAppearance, TokenTextColor } from '../text';
import { css, styled, ThemeProvider } from '../theme-provider';
import { Checkbox } from '../checkbox';
import { Container, ContainerGutter } from '../container';
import { getCommonProps } from '../helpers/common-props';
import { useState } from 'react';
import { getAuxillaryColor } from '../helpers/input';
const ANIMATION_FUNCTION = '0.3s cubic-bezier(0.14, 1.12, 0.67, 0.99)';
const StyledOverlay = styled.div.withConfig({ displayName: "StyledOverlay", componentId: "sc-s4sgrn" }) `position:absolute;z-index:1;width:100%;padding:${({ theme }) => theme.size.static250};background:${({ theme }) => theme.colors.background.primary};box-shadow:${({ theme }) => theme.colors.shadow.variant4};margin-top:${({ theme, singleOptionMode }) => singleOptionMode
    ? theme.size.static200
    : `${parseInt(theme.size.static200) * 2}px`};opacity:0;transform:translateX(-100vw);&:dir(rtl){transform:translateX(100vw);}&:focus-within{opacity:1;transform:translateX(0);}top:${({ singleOptionMode }) => (singleOptionMode ? '100%' : 'initial')};`;
const StyledLegend = styled.legend.withConfig({ displayName: "StyledLegend", componentId: "sc-oqxd84" }) `position:absolute;opacity:0;transform:translateX(-100vw);&:dir(rtl){transform:translateX(100vw);}`;
const StyledText = styled(Text).withConfig({ displayName: "StyledText", componentId: "sc-1s1i3rm" }) `transition:${({ theme }) => `color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};`;
const StyledLabelWrapper = styled.div.withConfig({ displayName: "StyledLabelWrapper", componentId: "sc-1iy2nvy" }) `display:flex;align-items:center;cursor:${({ internalDisabledProp }) => !internalDisabledProp ? 'pointer' : 'not-allowed'};${({ internalDisabledProp }) => !internalDisabledProp &&
    css `
			:hover ${StyledText} {
				color: ${({ theme }) => theme.colors.content.hover};
			}
		`}`;
const StyledIcon = styled.span.withConfig({ displayName: "StyledIcon", componentId: "sc-1d1jqe5" }) `position:relative;flex:0 0 auto;overflow:visible;text-align:center;transition:${({ theme }) => `transform ${ANIMATION_FUNCTION}, color ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};line-height:0;`;
const StyledCountAndIconWrapper = styled.span.withConfig({ displayName: "StyledCountAndIconWrapper", componentId: "sc-136w00o" }) `display:flex;align-items:center;${({ isFloating }) => isFloating &&
    css `
			position: absolute;
			top: ${({ theme }) => `calc(50% - ${theme.size.static200});`};
			right: ${({ theme }) => theme.size.static250};
		`}`;
const StyledLabelText = styled.span.withConfig({ displayName: "StyledLabelText", componentId: "sc-1q4xfzd" }) `font-size:${({ theme }) => theme.fonts.label[100].fontSize};width:100%;display:block;margin:0;padding:0;color:${({ isFloating, theme }) => isFloating ? 'transparent' : theme.colors.content.active};max-height:${({ isFloating, theme }) => isFloating ? '0' : `${theme.size.static250}`};transition:${({ theme }) => `max-height ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};`;
const StyledLabelTextWithFloating = styled.span.withConfig({ displayName: "StyledLabelTextWithFloating", componentId: "sc-19gmkxk" }) `transition:${({ theme }) => `all ${theme.animation.timingFunction.cubicOut} ${theme.animation.duration.color}`};position:absolute;top:50%;transform:translateY(-50%);color:${({ theme }) => theme.colors.content.primary};${({ hasValue, theme }) => {
    // Label-style, above the input
    if (hasValue) {
        return css `
				top: ${({ theme }) => theme.size.static100};
				transform: translateY(0);
				cursor: initial;
				font-size: ${theme.fonts.label[100].fontSize};
				padding: 0;
			`;
    }
    // Placeholder style, "inside" the input
    return css `
			font-size: ${theme.fonts.copy[200].fontSize};
		`;
}}`;
const StyledIconWrapper = styled.span.withConfig({ displayName: "StyledIconWrapper", componentId: "sc-1wmiix1" }) `position:relative;display:flex;align-items:center;justify-content:center;height:${({ theme }) => theme.size.static350};`;
const StyledInteractionWrapper = styled.div.withConfig({ displayName: "StyledInteractionWrapper", componentId: "sc-13kpulw" }) `display:grid;grid-column-gap:${({ theme }) => theme.size.static150};grid-template-columns:auto max-content;align-items:center;width:100%;min-height:${({ theme }) => theme.size.static350};pointer-events:${({ internalDisabledProp }) => !internalDisabledProp ? 'all' : 'none'};color:${({ theme, internalDisabledProp }) => theme.colors.content[internalDisabledProp ? 'disabled' : 'primary']};`;
const StyledCount = styled.span.withConfig({ displayName: "StyledCount", componentId: "sc-trvy7l" }) `box-sizing:content-box;margin-inline-start:${({ theme }) => theme.size.static200};min-width:1em;line-height:1em;padding:${({ theme }) => theme.size.static100};border-radius:100px;font-size:${({ theme }) => theme.fonts.label[100].fontSize};text-align:center;color:${({ theme }) => theme.colors.content.inverted};background:${({ theme }) => theme.colors.signal.neutral};height:fit-content;width:fit-content;`;
const StyledMultiSelect = styled.fieldset.withConfig({ displayName: "StyledMultiSelect", componentId: "sc-3rccn8" }) `margin:0;padding:${({ theme }) => theme.size.static200} ${({ theme }) => theme.size.static250};opacity:unset;position:relative;box-shadow:0 0 0 ${({ theme }) => theme.size.border.light} ${getAuxillaryColor};border:none;border-radius:${({ theme }) => theme.size.static150};cursor:${({ disabled }) => (!disabled ? 'pointer' : 'not-allowed')};${({ singleOptionMode, theme }) => singleOptionMode &&
    css `
			height: 50px; // static size of the input field, we don't have a token to represent this number currently; add in the future if needed
			display: flex;
			flex-direction: column;
			justify-content: center;
			gap: ${theme.size.static100};
			width: 100%;
		`} ${({ disabled, theme }) => !disabled &&
    css `
			:hover {
				transition: box-shadow 300ms ease;
				-webkit-transition: box-shadow 300ms ease;
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.light}
					${({ theme }) => theme.colors.border.hover};
				${StyledIcon} {
					color: ${theme.colors.button.tertiary.active};
				}

				${StyledLabelWrapper} {
					color: ${theme.colors.content.hover};
				}
			}

			:focus-within {
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
					${({ theme }) => theme.colors.focus.main};
				${StyledIcon} {
					transform: rotateX(180deg);
				}
				${StyledLabelTextWithFloating} {
					top: ${({ theme }) => theme.size.static100};
					transform: translateY(0);
					font-size: ${({ theme }) => theme.fonts.label[100].fontSize};
				}
			}
		`} ${({ disabled, theme, isFloating }) => !disabled &&
    isFloating &&
    css `
			/* On focus / while typing: Label-style, above the input */
			&:focus-within ${StyledLabelText} {
				max-height: ${theme.size.static250};
			}
		`} ${({ disabled, theme, isFloating }) => disabled &&
    css `
			${StyledLabelText} {
				color: ${isFloating ? 'transparent' : theme.colors.content.disabled};
			}
			${StyledLabelTextWithFloating} {
				color: ${isFloating ? theme.colors.content.disabled : 'initial'};
			}
		`} ${({ hasValue, theme }) => hasValue &&
    css `
			${StyledLabelText} {
				max-height: ${theme.size.static250};
			}
		`}`;
const StyledSingleOption = styled.div.withConfig({ displayName: "StyledSingleOption", componentId: "sc-vdv1yq" }) `color:${({ theme }) => theme.colors.button.primary.default};font-weight:${({ isActive }) => (isActive ? 'bold' : 'normal')};cursor:pointer;`;
const StyledResults = styled.span.withConfig({ displayName: "StyledResults", componentId: "sc-8hp6d1" }) `color:${({ theme }) => theme.colors.content.secondary};margin-left:${({ theme }) => theme.size.static150};`;
/* The onMouseDown preventDefault is needed to ensure that the focus will
    not be removed from the checkbox when clicked */
const handleCheckboxOnMouseDown = (event) => {
    event.preventDefault();
    event.currentTarget.focus();
};
const handleOverlayOnMouseDown = (event) => {
    event.preventDefault();
};
const handleNewValue = (value, optionKey) => {
    const newValue = [...value];
    if (value.includes(optionKey)) {
        newValue.splice(value.indexOf(optionKey), 1);
    }
    else {
        newValue.push(optionKey);
    }
    return newValue;
};
const handleMouseDown = (event) => {
    /* The onMouseDown preventDefault is needed to ensure that the focus will not be removed from the InteractionWrapper when clicked */
    event.preventDefault();
};
export const MultiSelect = (props) => {
    const { defaultValue, label, name, onChange, onClick, onOptionClick, options, value, disabled, singleOptionMode, defaultSingleOption, isFloating, results } = props, rest = __rest(props, ["defaultValue", "label", "name", "onChange", "onClick", "onOptionClick", "options", "value", "disabled", "singleOptionMode", "defaultSingleOption", "isFloating", "results"]);
    const commonProps = getCommonProps(props);
    const firstOption = React.useRef(null);
    const multiSelect = React.useRef(null);
    const labelRef = React.createRef();
    const [internalValue, setInternalValue] = React.useState(props.defaultValue || []);
    const [selectedOption, setSelectedOption] = useState(defaultSingleOption);
    const [labelHeight, setLabelHeight] = React.useState(0);
    const [hasValue, setHasValue] = React.useState(!!selectedOption || !!defaultSingleOption || false);
    const handleClick = React.useCallback((event) => {
        if (firstOption.current && multiSelect.current) {
            const elementWithFocus = multiSelect.current.querySelector(':focus');
            if (elementWithFocus) {
                elementWithFocus.blur();
                return;
            }
            firstOption.current.focus();
        }
        onClick && onClick(event);
    }, [onClick]);
    React.useEffect(() => {
        var _a;
        setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0);
    }, [labelRef]);
    React.useEffect(() => {
        setHasValue(!!selectedOption);
    }, [selectedOption]);
    const handleResize = React.useCallback(() => { var _a; return setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0); }, [labelRef]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const handleCheckboxClick = React.useCallback((e, key) => {
        e.stopPropagation();
        onOptionClick && onOptionClick(key);
    }, [onOptionClick]);
    const handleCheckboxChange = React.useCallback((key) => {
        if (value) {
            return;
        }
        const newInternalValue = handleNewValue(internalValue, key);
        setInternalValue(newInternalValue);
        onChange && onChange(newInternalValue);
    }, [onChange, value, internalValue]);
    const handleSingleOptionClick = React.useCallback((label) => {
        setSelectedOption(label);
        onChange && onChange(label);
    }, [onChange]);
    const optionsElement = Object.entries(options).map((entry, index) => {
        const [key, elementLabel] = entry;
        const formattedElementLabelWith = results ? (React.createElement("div", { key: key },
            elementLabel,
            React.createElement(StyledResults, null,
                "(",
                results[index],
                ")"))) : (elementLabel);
        if (singleOptionMode) {
            return (
            // We use tabindex here because without this attribute we won't be able to invoke the focus event and dropdown list won't be opened.
            React.createElement(StyledSingleOption, { key: key, ref: index === 0 ? firstOption : undefined, isActive: selectedOption === elementLabel, tabIndex: 0, onClick: () => handleSingleOptionClick(elementLabel) }, elementLabel));
        }
        else {
            return (React.createElement(Checkbox, { key: key, value: key, label: formattedElementLabelWith, ref: index === 0 ? firstOption : undefined, defaultChecked: defaultValue && defaultValue.includes(key), checked: value && value.includes(key), onLabelMouseDown: handleCheckboxOnMouseDown, onClick: (e) => handleCheckboxClick(e, key), onChange: () => handleCheckboxChange(key), name: name, disabled: disabled }));
        }
    });
    const count = value ? value.length : internalValue.length;
    return (React.createElement(StyledMultiSelect, Object.assign({ ref: multiSelect, disabled: disabled, singleOptionMode: singleOptionMode, hasValue: hasValue, isFloating: isFloating, onClick: handleClick }, commonProps, rest),
        label && singleOptionMode && (React.createElement(StyledLabelText, { ref: labelRef, isFloating: isFloating }, label)),
        React.createElement(StyledLegend, null, label),
        label && singleOptionMode && isFloating && (React.createElement(StyledLabelTextWithFloating, { hasValue: hasValue, labelHeight: labelHeight }, label)),
        React.createElement(StyledInteractionWrapper, { onMouseDown: handleMouseDown, "aria-hidden": true, internalDisabledProp: disabled },
            React.createElement(StyledLabelWrapper, { internalDisabledProp: disabled },
                React.createElement(StyledText, { tag: TextTag.span, color: TokenTextColor.inherit, appearance: TokenTextAppearance.copy200, staticSize: true }, singleOptionMode ? selectedOption : label),
                count > 0 && (React.createElement(ThemeProvider, { theme: "main" },
                    React.createElement(StyledCount, null, count)))),
            React.createElement(StyledCountAndIconWrapper, { isFloating: isFloating },
                React.createElement(StyledIconWrapper, null,
                    React.createElement(StyledIcon, null,
                        React.createElement(ChevronDown, { variant: "default", ariaHidden: true }))))),
        React.createElement(ThemeProvider, { theme: "main" },
            React.createElement(StyledOverlay, { onMouseDown: handleOverlayOnMouseDown, singleOptionMode: singleOptionMode },
                React.createElement(Container, { gutter: ContainerGutter.static200, wrap: "always" }, optionsElement)))));
};
